import { Core } from "@faire/design-tokens";
import { getCountryName } from "@faire/help-center-utils/lib/getCountryName";
import { getBrand } from "@faire/help-center-utils/serialized-data/getBrand";
import { getSettingShipWithFaireEnabledCountries } from "@faire/help-center-utils/settings/getSettingShipWithFaireEnabledCountries";
import { BoldFormatting } from "@faire/help-center/components/utils/BoldFormatting";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Lightbulb } from "@faire/web--source/slate/icons/foundation/Lightbulb";
import { Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import React from "react";
import styled from "styled-components";

export const SWFBlurb: React.FC = () => {
  const { isSWFAvailable, countryName } = useSWFBlurbData();

  return (
    <Wrapper>
      <Lightbulb fontSize="24px" />
      <Spacer width="2x" />
      {isSWFAvailable ? (
        <Typography>
          <StrictLocalMsg
            defaultMessage="Ship with Faire is available for brands in your country (<boldFormatting>{countryFullyTranslatedSentence}</boldFormatting>)."
            values={{
              countryFullyTranslatedSentence: countryName,
              boldFormatting: BoldFormatting,
            }}
          />
        </Typography>
      ) : (
        <Typography>
          <StrictLocalMsg
            defaultMessage="Ship with Faire isn’t available for brands in your country (<boldFormatting>{countryFullyTranslatedSentence}</boldFormatting>)."
            values={{
              countryFullyTranslatedSentence: countryName,
              boldFormatting: BoldFormatting,
            }}
          />
        </Typography>
      )}
    </Wrapper>
  );
};

const useSWFBlurbData = () => {
  const brand = getBrand();
  const { strictLocalize } = useStrictLocalization();
  const brandShipsFromCountryISO = getBrandShipsFromCountryISO(brand);
  const isSWFAvailable = getIsSWFAvailable(brandShipsFromCountryISO);
  const countryName = getCountryName(brandShipsFromCountryISO, strictLocalize);

  return {
    isSWFAvailable,
    countryName,
  };
};

const getBrandShipsFromCountryISO = (brand: IBrand | undefined) => {
  return (
    (brand?.ships_from_country as ISO3166Alpha3) ??
    ISO3166Alpha3.ISO3166ALPHA3_UNKNOWN
  );
};

const getIsSWFAvailable = (brandShipsFromCountryISO: ISO3166Alpha3) => {
  return getSettingShipWithFaireEnabledCountries().includes(
    brandShipsFromCountryISO
  );
};

const Wrapper = styled(Row).attrs({ justify: "center", align: "center" })`
  background-color: ${Core.surface.subdued};
  padding: ${getSpacing("3x")};
`;
